import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  ThematicIcon,
  Tooltip,
  Typography,
  WorkspaceIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox, { ListboxDataItemBase } from '@/components/Listbox';
import { useClients } from '@/features/app/api/get-clients';
import { useWorkspaces } from '@/features/app/api/get-workspaces';
import { TClient, TWorkspaceEntity, WorkspaceTypeEnum } from '@/features/app/types';
import { workspaceClientMap } from '@/features/app/utils/workspace-client-map';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { TFilterProps } from '@/types';

import { TActiveOptions } from '../active-options/active-options';
import { ActiveFacets } from '../active-options/ActiveFacets';
import { OptionsFooter } from '../active-options/OptionsFooter';
import { TFacetsResult } from '../active-options/use-mention-facets';

function ThematicWorkspaceIcon() {
  const { t } = useTranslation();
  return (
    <ThematicIcon
      role="img"
      aria-label={t('Temático')}
      size={IconSizeEnum.SM}
      className="!text-extra-80"
    />
  );
}

const THEMATIC_ICON = <ThematicWorkspaceIcon />;

type TWorkspaceGroup = {
  label: string;
  image?: string;
  clientId: string;
  values: ListboxDataItemBase[];
};

function groupWorkspacesByClient(
  workspaceList: TWorkspaceEntity[],
  clientsData: TClient[]
): TWorkspaceGroup[] {
  const clientIndex = clientsData.reduce(
    (acc, currentClient) => {
      if (currentClient.state === 'active') {
        acc[currentClient.id] = currentClient;
      }
      return acc;
    },
    {} as Record<string, TClient>
  );

  return Object.entries(
    workspaceList.reduce(
      (acc, { id, name, parameters, client_id, workspace_type }) => {
        const client = acc[client_id];

        const workspace: ListboxDataItemBase = {
          label: name,
          value: id,
          image: parameters?.logo_positive as string,
          key: id,
          icon: workspace_type === WorkspaceTypeEnum.THEMATIC ? THEMATIC_ICON : undefined,
        };

        if (client) client.push(workspace);
        else {
          acc[client_id] = [workspace];
        }

        return acc;
      },
      {} as Record<string, ListboxDataItemBase[]>
    )
  )
    .map(([clientId, worskspaces]) => ({
      label: clientIndex[clientId]?.name ?? '',
      image: clientIndex[clientId]?.parameters?.logo_positive as string,
      clientId,
      values: worskspaces.sort((optionA, optionB) => optionA.label.localeCompare(optionB.label)),
    }))
    .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));
}

const WORKSPACE_LISTBOX_IDENTIFIERS = {
  key: 'value',
  value: 'value',
  label: 'label',
  children: 'values',
};

type WorkspaceActiveOptionsProps = {
  facets: TFacetsResult;
  catalogue: TWorkspaceEntity[];
  clientsData: TClient[];
  isDark?: boolean;
  selected: string[];
  onChange: (next: string[]) => void;
  onClose?: () => void;
};

/** Mounted only while the dropdown is open, so opening it is what refreshes stale options. */
function WorkspaceActiveOptions({
  facets,
  catalogue,
  clientsData,
  isDark,
  selected,
  onChange,
  onClose,
}: WorkspaceActiveOptionsProps) {
  const { t } = useTranslation();
  const { data, isFetching, isError } = facets;

  const { groups, activeCount } = useMemo(() => {
    const live = new Set(data?.workspaces ?? []);
    const shown = new Set(selected);
    const active = catalogue.filter(({ id, state }) => state === 'active' && live.has(id));
    active.forEach(({ id }) => shown.add(id));

    const listed = catalogue.filter(({ id }) => shown.has(id));
    const known = new Set(catalogue.map(({ id }) => id));
    const unknown = selected.filter(id => !known.has(id));

    const grouped = groupWorkspacesByClient(listed, clientsData);
    if (unknown.length) {
      grouped.push({
        label: t('(no disponible)'),
        clientId: '',
        values: unknown.map(id => ({ label: t('(no disponible)'), value: id, key: id })),
      });
    }

    return { groups: grouped, activeCount: active.length };
  }, [data, catalogue, clientsData, selected, t]);

  return (
    <Listbox
      dark={isDark}
      mode="multiple"
      items={groups}
      onChange={onChange}
      identifiers={WORKSPACE_LISTBOX_IDENTIFIERS}
      selected={selected}
      onClose={onClose}
      listFooter={
        <OptionsFooter
          isDark={isDark}
          isFetching={isFetching}
          isError={isError}
          isEmpty={activeCount === 0}
        />
      }
    />
  );
}

export function MentionFilterWorkspaces({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  activeOptions,
}: TFilterProps & { activeOptions?: TActiveOptions }) {
  const { t } = useTranslation();
  const { workspaces: state, setFilters: setState } = useProgramBroadcastFilters();
  const { data: items = [] } = useWorkspaces();
  const { data: clientsData = [] } = useClients();

  const workspaces = useMemo(() => {
    if (!clientsData || !items) return [];

    return groupWorkspacesByClient(
      items.filter(({ state }) => state === 'active'),
      clientsData
    );
  }, [items, clientsData]);

  const workspaceToClientMap = useMemo(() => workspaceClientMap(items ?? []), [items]);

  const handleChangeState = (selectedValues: string[]) => {
    const workspacesFilter = selectedValues;

    const clientsFilter = Array.from(
      new Set(selectedValues.map(value => workspaceToClientMap[value]).filter(Boolean))
    );
    console.info('workspaces changed: ', workspacesFilter);
    setState({ workspaces: workspacesFilter, clients: clientsFilter });
  };

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Espacios de trabajo')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && workspaces.length !== 0 && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark }
        )}
      >
        <ToolTipWrapper>
          <WorkspaceIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Espacios de trabajo')}
          </Typography>
        )}
        {state.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {state.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:w-max md:shadow-full-medium">
          {activeOptions ? (
            <ActiveFacets activeOptions={activeOptions}>
              {facets => (
                <WorkspaceActiveOptions
                  facets={facets}
                  catalogue={items ?? []}
                  clientsData={clientsData ?? []}
                  isDark={isDark}
                  selected={state}
                  onChange={handleChangeState}
                  onClose={onClose}
                />
              )}
            </ActiveFacets>
          ) : (
            <Listbox
              dark={isDark}
              mode="multiple"
              items={workspaces}
              onChange={handleChangeState}
              identifiers={WORKSPACE_LISTBOX_IDENTIFIERS}
              selected={state}
              onClose={onClose}
            />
          )}
        </div>
      )}
    </div>
  );
}
